import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  Select,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Swal from "sweetalert2";
import AppointmentsApi from "../../../../Classes/API/Appointments/AppointmentsApi";

const TherapistDetailsPopUp = ({
  show,
  onClose,
  therapists,
  appDayId,
  setIsLoaded,
}) => {
  const [sourceTherapist, setSourceTherapist] = useState("");
  const [destTherapist, setDestTherapist] = useState("");
  const [actionType, setActionType] = useState("move");
  const [loading, setLoading] = useState(false);

  const showToast = (icon, title) => {
    Swal.fire({
      icon: icon,
      title: title,
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.style.zIndex = "10000";
        toast.addEventListener("mouseenter", Swal.stopTimer);
        toast.addEventListener("mouseleave", Swal.resumeTimer);
      },
    });
  };

  const handleSourceChange = (e) => {
    const val = e.target.value;
    setSourceTherapist(val);
    if (val === destTherapist) {
      setDestTherapist("");
    }
  };

  const handleConfirm = async () => {
    if (!sourceTherapist || !destTherapist) {
      showToast("error", "Please select both therapists");
      return;
    }

    setLoading(true);
    try {
      Swal.fire({
        title: "Processing...",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        didOpen: (toast) => {
          toast.style.zIndex = "10000";
          Swal.showLoading();
        },
      });

      const payload = {
        dayId: appDayId,
        sourceTherapist: sourceTherapist.therapistId,
        destinationTherapist: destTherapist.therapistId,
        mode: actionType,
      };

      const res = await AppointmentsApi.TransferTherapistSessions(payload);

      if (res.status === "Success") {
        showToast("success", res.message || "Action completed successfully");
        onClose();
        setIsLoaded(false);
      } else {
        showToast("error", res.message || "Failed to complete action");
      }
    } catch (error) {
      showToast("error", "Internal Server Error");
    } finally {
      setLoading(false);
      Swal.close();
    }
  };

  return (
    <Dialog
      open={show}
      onClose={loading ? null : onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        <MDTypography variant="h5">Transfer/Switch Sessions</MDTypography>
      </DialogTitle>

      <DialogContent>
        <MDBox mt={2} display="flex" flexDirection="column" gap={3}>
          <FormControl fullWidth disabled={loading}>
            <InputLabel id="source-therapist-label">
              Source Therapist (From)
            </InputLabel>
            <Select
              labelId="source-therapist-label"
              id="source-therapist-select"
              value={sourceTherapist}
              label="Source Therapist (From)"
              onChange={handleSourceChange}
              sx={{
                height: "45px",
                "& .MuiSelect-select": {
                  height: "45px",
                  display: "flex",
                  alignItems: "center",
                  boxSizing: "border-box",
                },
              }}
            >
              {therapists?.map((t) => (
                <MenuItem key={t.id || t.therapistId} value={t}>
                  {t.therapistName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth disabled={!sourceTherapist || loading}>
            <InputLabel id="dest-therapist-label">
              Destination Therapist (To)
            </InputLabel>
            <Select
              labelId="dest-therapist-label"
              id="dest-therapist-select"
              value={destTherapist}
              label="Destination Therapist (To)"
              onChange={(e) => setDestTherapist(e.target.value)}
              sx={{
                height: "45px",
                "& .MuiSelect-select": {
                  height: "45px",
                  display: "flex",
                  alignItems: "center",
                  boxSizing: "border-box",
                },
              }}
            >
              {therapists
                ?.filter((t) => t !== sourceTherapist)
                .map((t) => (
                  <MenuItem key={t.id || t.therapistId} value={t}>
                    {t.therapistName}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <FormControl disabled={loading}>
            <FormLabel id="action-mode-group-label">
              <MDTypography variant="button" fontWeight="bold">
                Action Mode
              </MDTypography>
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby="action-mode-group-label"
              value={actionType}
              onChange={(e) => setActionType(e.target.value)}
            >
              <FormControlLabel
                value="move"
                control={<Radio />}
                label="Move All"
              />
              <FormControlLabel
                value="switch"
                control={<Radio />}
                label="Switch Schedules"
              />
            </RadioGroup>
          </FormControl>
        </MDBox>
      </DialogContent>

      <DialogActions>
        <MDButton onClick={onClose} color="secondary" disabled={loading}>
          Cancel
        </MDButton>
        <MDButton
          onClick={handleConfirm}
          variant="gradient"
          color="info"
          disabled={!sourceTherapist || !destTherapist || loading}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Confirm Action"
          )}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

export default TherapistDetailsPopUp;
